import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import {
  HiOutlineArrowLongRight,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineShieldCheck,
} from 'react-icons/hi2';
import { Link, useLocation } from 'wouter';

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const { login, register } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [showPw, setShowPw] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        toast({
          title: 'Welcome back!',
          description: 'You are now signed in.',
        });
        // Small delay to ensure state updates before redirect
        setTimeout(() => setLocation('/dashboard'), 100);
      } else {
        await register(
          form.name,
          form.email,
          form.password,
          form.phone || undefined
        );
        toast({
          title: 'Account created!',
          description: 'Welcome to windows cleaning School.',
        });
        setTimeout(() => setLocation('/dashboard'), 100);
      }
    } catch (err) {
      toast({
        title: mode === 'login' ? 'Login failed' : 'Registration failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#f7fafd] flex flex-col">
      {/* Nav */}
      <div className="px-6 py-5 flex items-center justify-between">
        <Link href="/">
          <span className="font-bebas text-xl tracking-[0.08em] text-[#0a2540] cursor-pointer">
            windows cleaning <span className="text-[#0ea5e9]">School</span>
          </span>
        </Link>
        <Link href="/">
          <span className="font-sans text-sm text-[#94a3b8] hover:text-[#0ea5e9] transition-colors cursor-pointer">
            ← Back to site
          </span>
        </Link>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          {/* Card */}
          <div
            className="bg-white rounded-2xl p-10"
            style={{ boxShadow: '0 24px 80px rgba(10,37,64,0.15)' }}
          >
            <div className="flex items-center gap-2 mb-8">
              <div className="w-9 h-9 rounded-xl bg-[#e0f4fd] flex items-center justify-center">
                <HiOutlineShieldCheck className="w-5 h-5 text-[#0369a1]" />
              </div>
              <div>
                <div className="font-bebas text-[1.6rem] leading-none text-[#0a2540] tracking-[0.04em]">
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                </div>
                <div className="font-sans text-[0.75rem] text-[#94a3b8] font-light">
                  {mode === 'login'
                    ? 'Access your training dashboard'
                    : 'Join windows cleaning School'}
                </div>
              </div>
            </div>

            {/* Admin hint */}
            {mode === 'login' && (
              <div className="mb-6 p-3 rounded-xl bg-[#f7fafd] text-[0.78rem] font-sans text-[#94a3b8] leading-relaxed">
                <span className="font-rajdhani font-bold text-[#0369a1] uppercase tracking-wider text-[0.62rem]">
                  Admin login
                </span>
                <br />
                admin@windowcleaningschool.com / admin123
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {mode === 'register' && (
                <div>
                  <label className="font-rajdhani font-bold text-[0.64rem] tracking-[0.24em] uppercase text-[#94a3b8] block mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set('name')}
                    placeholder="John Smith"
                    required
                    className="w-full h-12 px-4 rounded-xl bg-[#f7fafd] text-[#0d1e30] font-sans text-sm outline-none focus:ring-2 focus:ring-[#0ea5e9]/30"
                  />
                </div>
              )}

              <div>
                <label className="font-rajdhani font-bold text-[0.64rem] tracking-[0.24em] uppercase text-[#94a3b8] block mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@example.com"
                  required
                  className="w-full h-12 px-4 rounded-xl bg-[#f7fafd] text-[#0d1e30] font-sans text-sm outline-none focus:ring-2 focus:ring-[#0ea5e9]/30"
                />
              </div>

              <div>
                <label className="font-rajdhani font-bold text-[0.64rem] tracking-[0.24em] uppercase text-[#94a3b8] block mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={set('password')}
                    placeholder={
                      mode === 'register'
                        ? 'Minimum 6 characters'
                        : 'Your password'
                    }
                    required
                    className="w-full h-12 px-4 pr-12 rounded-xl bg-[#f7fafd] text-[#0d1e30] font-sans text-sm outline-none focus:ring-2 focus:ring-[#0ea5e9]/30"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#0369a1] cursor-pointer"
                  >
                    {showPw ? (
                      <HiOutlineEyeSlash className="w-5 h-5" />
                    ) : (
                      <HiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {mode === 'register' && (
                <div>
                  <label className="font-rajdhani font-bold text-[0.64rem] tracking-[0.24em] uppercase text-[#94a3b8] block mb-1.5">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="+1 (555) 000-0000"
                    className="w-full h-12 px-4 rounded-xl bg-[#f7fafd] text-[#0d1e30] font-sans text-sm outline-none focus:ring-2 focus:ring-[#0ea5e9]/30"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-13 mt-2 rounded-full font-rajdhani font-bold tracking-[0.18em] text-[0.8rem] uppercase text-white flex items-center justify-center gap-2 cursor-pointer transition-all hover:-translate-y-0.5 disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9, #0369a1)',
                  boxShadow: '0 8px 28px rgba(14,165,233,0.4)',
                  height: '52px',
                }}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {mode === 'login' ? 'Sign In' : 'Create Account'}{' '}
                    <HiOutlineArrowLongRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <span className="font-sans text-[0.82rem] text-[#94a3b8]">
                {mode === 'login'
                  ? 'No account yet? '
                  : 'Already have an account? '}
              </span>
              <button
                onClick={() =>
                  setMode(m => (m === 'login' ? 'register' : 'login'))
                }
                className="font-rajdhani font-bold text-[0.82rem] text-[#0369a1] hover:text-[#0ea5e9] transition-colors cursor-pointer"
              >
                {mode === 'login' ? 'Create one free' : 'Sign in instead'}
              </button>
            </div>
          </div>

          <p className="text-center font-sans text-[0.72rem] text-[#94a3b8] mt-6 leading-relaxed">
            Admin credentials give access to the full management panel.
            <br />
            Student accounts show your own bookings & progress.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
